import apiClient from '../apiClient';
import {
  mapRouteCollectionFromApiToVm,
  mapRouteFromApiToVm,
  mapUserRouteProgressCollectionFromApiToVm,
} from './route.mapper';
import { Route, UserRouteProgress } from './route.vm';
import { RouteProgressStatus } from './types';

export const routesService = {
  // ROUTES

  getRoutes: async () => {
    const response = await apiClient.get('/routes');
    return mapRouteCollectionFromApiToVm(response.data);
  },

  getRoute: async (id: string) => {
    if (!id) {
      throw new Error('Route Id not provided');
    }
    const response = await apiClient.get(`/routes/${id}`);
    return mapRouteFromApiToVm(response.data);
  },

  createRoute: async (route: FormData) => {
    if (!route) {
      throw new Error('Route not provided');
    }
    try {
      const response = await apiClient.post('/routes', route, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return mapRouteFromApiToVm(response.data);
    } catch (e) {
      throw new Error('Failed to create route');
    }
  },

  updateRoute: async (routeId: string, route: FormData) => {
    if (!routeId) {
      throw new Error('Route Id not provided');
    }
    if (!route) {
      throw new Error('Route data not provided');
    }
    try {
      const response = await apiClient.patch(`/routes/${routeId}`, route, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return mapRouteFromApiToVm(response.data);
    } catch (e) {
      throw new Error('Failed to update route');
    }
  },

  deleteRoute: async (routeId: string) => {
    if (!routeId) {
      throw new Error('Route Id not provided');
    }
    try {
      const response = await apiClient.delete(`/routes/${routeId}`);
      return response.data;
    } catch (e) {
      throw new Error('Failed to delete route');
    }
  },

  reorder: async (newRoutesOrdering: Array<{ routeId: Route['id']; newOrder: number }>) => {
    if (!newRoutesOrdering) {
      throw new Error('New routes ordering not provided');
    }
    try {
      const response = await apiClient.patch(`/routes/reorder`, { routes: newRoutesOrdering });
      return response.data;
    } catch (e) {
      throw new Error('Failed to reorder');
    }
  },

  // PROGRESS

  getUsersProgressesByRoute: async (routeId: Route['id']) => {
    if (!routeId) {
      throw new Error('Route Id not provided');
    }
    try {
      const response = await apiClient.get(`/user-progress`, {
        params: { routeId },
      });
      return mapUserRouteProgressCollectionFromApiToVm(response.data);
    } catch (e) {
      throw new Error('Failed to get user progress');
    }
  },

  createRouteProgress: async (routeId: string) => {
    if (!routeId) {
      throw new Error('Route Id not provided');
    }
    try {
      const response = await apiClient.post(`/user-progress`, {
        routeId,
        status: 'IN_PROGRESS' as RouteProgressStatus,
      });
      return response.data;
    } catch (e) {
      throw new Error('Failed to create route progress');
    }
  },

  updateRouteProgress: async (userRouteProgressId: string, userProgressData: Partial<UserRouteProgress>) => {
    if (!userRouteProgressId) {
      throw new Error('User route progress Id not provided');
    }
    try {
      const response = await apiClient.patch(`/user-progress/${userRouteProgressId}`, userProgressData);
      return response.data;
    } catch (e) {
      throw new Error('Failed to update route progress');
    }
  },

  updateUserProgressLastInteraction: async (userRouteProgressId: Route['userRouteProgressId']) => {
    if (!userRouteProgressId) {
      throw new Error('User route progress Id not provided');
    }
    try {
      const response = await apiClient.patch(`/user-progress/${userRouteProgressId}`, {
        lastVisitedAt: new Date().toISOString(),
      });
      return response.data;
    } catch (e) {
      throw new Error('Failed to update last interaction');
    }
  },
    getNextContent: async (userRouteProgressId: Route['userRouteProgressId']) => {
    if (!userRouteProgressId) {
      throw new Error('User route progress Id not provided');
    }
    try {
      const response = await apiClient.get(`/user-progress/${userRouteProgressId}/next-content`);
      return response.data;
    } catch (e) {
      throw new Error('Failed to get next content');
    }
  },
};
